/** Strings of the work-item detail panels (sidebar, plan, agent work, contract, …): `wiDetailEn` is the source, `wiDetailCs` must have the same keys. */
export const wiDetailEn = {
  wiHandoffs: {
    kinds: {
      started: "Started",
      submitted: "Handed over",
      returned: "Sent back",
      waiting: "Waiting",
      merged: "Merged",
      tested: "Tested",
      escalated: "Needs a person",
      note: "Note",
    },
    agent: "Agent",
    details: "Details",
    whatsLeft: "What's left:",
    empty: "The agents haven't said anything here.",
    question: "Question",
    recommended: "Recommended",
    due: "Due",
    openTitle: "Waiting for a person",
  },
  wiMerged: {
    mergedInto: "Merged into",
    on: "on",
    undoMerge: "Undo merge",
    undone: "Merge undone",
    undoFailed: "Undo failed",
  },
  wiContract: {
    title: "Acceptance criteria",
    ariaLabel: "Acceptance criteria, one per line",
    save: "Save criteria",
    cancel: "Cancel",
    saved: "Acceptance criteria saved",
    saveFailed: "Couldn't save the criteria",
    byPerson: "Written by a person.",
    byAgent: "Written by an agent from the ticket.",
    againstThese: "The agents build, review and test against these.",
    proposed: "Proposed by an agent from the ticket, waiting for approval.",
    groupDev: "Development (Vratislav checks in the MR review)",
    groupTest: "Test (Prokop checks after the merge)",
    manual: "a person verifies by hand",
    pending:
      "None yet. Prokop derives them in the background; until then the description is what counts.",
    notDerived: "A person's own task: no criteria are derived, the description is the brief.",
    edit: "Edit criteria",
    approve: "Approve criteria",
    approveTitle:
      "Approve the proposed criteria as they are. Vratislav and Prokop stop negotiating them and the audit judges against them.",
    approved: "Acceptance criteria approved",
    approveFailed: "Couldn't approve the criteria",
    reopen: "Undo approval",
    reopenTitle: "The criteria go back to a proposal that Vratislav and Prokop settle.",
    reopened: "Approval undone",
    reopenFailed: "Couldn't undo the approval",
  },
  wiMergeRequests: {
    title: "Merge requests",
    none: "No merge request yet.",
    states: { opened: "open", merged: "merged", closed: "closed", locked: "locked" },
    placeholder: "MR link or !number",
    link: "Link",
    linking: "Linking…",
    linked: "Merge request linked",
    linkFailed: "Failed to link the merge request",
    unlink: "Not this ticket's",
    unlinked: "Merge request unlinked",
    unlinkFailed: "Failed to unlink the merge request",
  },
  wiRelations: {
    title: "Dependencies ({{count}})",
    labels: {
      blocks: "blocks",
      blocked_by: "blocked by",
      relates: "relates to",
      duplicates: "duplicates",
      duplicated_by: "duplicated by",
      precedes: "precedes",
      follows: "follows",
      copied_to: "copied to",
      copied_from: "copied from",
    },
    added: "Relation added",
    addFailed: "Failed to add relation",
    removed: "Relation removed",
    removeFailed: "Failed to remove relation",
    removeLink: "Remove link",
    remove: "Remove {{title}}",
    none: "No dependencies yet.",
    relationType: "Relation type",
    selectPlaceholder: "— select work item —",
    noWorkItems: "No work items",
    relatedItem: "Related work item",
    linkDependency: "Link dependency",
    linking: "Linking…",
    link: "Link",
  },
  wiDescHistory: {
    sources: {
      user: "edited",
      suggestion: "AI suggestion approved",
      agent: "changed by an agent",
      gitlab: "changed in GitLab",
      restore: "older version restored",
      system: "changed by Hub",
    },
    restored: "Description restored",
    restoreFailed: "Couldn't restore it",
    history: "History",
    title: "Description history",
    description: "Each version is the description as it read before the change listed with it.",
    loading: "Loading…",
    empty: "No earlier versions yet. Every change from now on keeps the text it replaced.",
    showChanges: "Show changes since",
    showVersion: "Show this version",
    restoring: "Restoring…",
    restore: "Restore this version",
    emptyVersion: "Empty.",
  },
  wiAgentWork: {
    loading: "Loading…",
    nothingYet: "Nothing yet; lines appear as the agent works.",
    tookOver: "You hold its development now",
    takeOverFailed: "Couldn't take it over",
    handedBack: "Handed back to Metoděj",
    handBackFailed: "Couldn't hand it back",
    workingInLair: "<b>{{agent}}</b> is working on it in {{user}}'s lair",
    lairSession: "Lair session <b>{{session}}</b> of {{user}} is working on it",
    working: "<b>{{agent}}</b> is working on it",
    since: "since <date/>",
    noAgent: "No agent is working on it right now.",
    stopWatching: "Stop watching",
    watch: "Watch",
    takingOver: "Taking over…",
    takeOver: "Take over development",
    openInLair: "Open in lair",
    handingBack: "Handing back…",
    handBack: "Hand back to Metoděj",
    nothingOpened: "Nothing opened?",
    getLair: "Get lair",
    orRun: ", or run:",
    copyCommand: "Copy the command",
  },
  wiSteps: {
    status: {
      proposed: "Proposed",
      pending: "To do",
      in_progress: "Running",
      verified: "Verified",
      rejected: "Gave up",
      dropped: "Dropped",
      promoted: "Own ticket",
    },
    stepTitle: "Step title",
    stepCriterion: "Step criterion",
    savingRetries: "Saving sends the step back for a fresh round.",
    save: "Save",
    cancel: "Cancel",
    earlierDescription: "Earlier description",
    earlierDescriptionHint: "The description changed after this step was verified",
    replacedOnActivation: "Replaced on activation",
    attempts_one: "{{count}} attempt",
    attempts_few: "{{count}} attempts",
    attempts_many: "{{count}} attempts",
    attempts_other: "{{count}} attempts",
    whatWasChecked: "What was checked",
    whatWentWrong: "What went wrong",
    edit: "Edit",
    editHint: "Rewrite the title or the criterion",
    retry: "Retry",
    promote: "Promote",
    promoteHint: "Make this a work item of its own",
    dropHint: "Not needed",
    drop: "Drop {{title}}",
    planActivated: "Plan activated",
    activateFailed: "Couldn't activate the plan",
    stepAdded: "Step added",
    addFailed: "Couldn't add the step",
    planUpdated: "Plan updated",
    updateFailed: "Couldn't update the step",
    stepUpdated: "Step updated",
    promoted: "Step is a work item of its own now",
    promoteFailed: "Couldn't promote the step",
    title: "Plan",
    progress: "{{verified}}/{{total}} verified",
    none: "No plan yet. An agent proposes one when it picks the ticket up; you can also break it down yourself below.",
    proposed_one:
      "{{count}} proposed step, nothing is worked until the plan is active. Vratislav reviews an agent's plan and activates it or sends it back; you can activate it yourself.",
    proposed_few:
      "{{count}} proposed steps, nothing is worked until the plan is active. Vratislav reviews an agent's plan and activates it or sends it back; you can activate it yourself.",
    proposed_many:
      "{{count}} proposed steps, nothing is worked until the plan is active. Vratislav reviews an agent's plan and activates it or sends it back; you can activate it yourself.",
    proposed_other:
      "{{count}} proposed steps, nothing is worked until the plan is active. Vratislav reviews an agent's plan and activates it or sends it back; you can activate it yourself.",
    replaces_one: "Activating drops the step they replace.",
    replaces_few: "Activating drops the steps they replace.",
    replaces_many: "Activating drops the steps they replace.",
    replaces_other: "Activating drops the steps they replace.",
    activating: "Activating…",
    activate: "Activate plan",
    stepPlaceholder: "Step",
    criterionPlaceholder: "What has to be true for it to count as done",
    adding: "Adding…",
    add: "Add",
  },
  wiSidebar: {
    noRuns: "No agent runs.",
    tok: "tok",
    cacheRead: "cache read",
    cacheWrite: "cache write",
    failed: "failed",
    running: "running",
    interrupted: "interrupted",
    interruptedHint: "Interrupted by a restart during a deploy, the work will resume",
    mr: "MR",
    estimateNote: "~ estimate at list price: the agent runs on a subscription.",
    billing: "Billing",
    billingMode: "Billing mode",
    byHours: "Bill by hours worked",
    fixedPrice: "Fixed delivery price",
    requiresEstimate: "Requires estimate",
    fixedAlwaysEstimated: "Fixed-price work is always estimated.",
    approvedOffer: "Approved offer (Netlion Cloud)",
    openOffer: "Open the approved offer ↗",
    save: "Save",
    invoicedUndone: "No longer marked as invoiced",
    undoFailed: "Undo failed",
    invoicedNote: "Marked as already invoiced on <date/> — won't be offered on the next report.",
    undo: "Undo",
    priority: "Priority",
    priorities: { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" },
    assignee: "Assignee",
    unassigned: "— unassigned —",
    noMembers: "No project members",
    handedToTester: "Handed to Tester",
    handToTesterFailed: "Couldn't hand it to the Tester",
    handingOver: "Handing over…",
    handToProkop: "Hand to Prokop",
    environment: "Environment",
    currentEnvironment: "Current environment",
    targetRelease: "Target Release",
    release: "Release",
    none: "— none —",
    noReleases: "No releases",
    changeRelease: "Change target release",
    repository: "Repository",
    projectRepo: "Project repository",
    changeRepository: "Change the repository the code work happens in",
    team: "Team",
    saveTeam: "Save team",
    roles: {
      manager: "Manager",
      developer: "Developer",
      tester: "Tester",
      maintainer: "Maintainer",
      productionTester: "Production tester",
      productionMaintainer: "Production maintainer",
    },
    noCandidates: "No candidates",
    onlyCandidate: "(only candidate)",
    locked:
      "Locked — only a project manager can change the team after the ticket leaves <em>{{state}}</em>.",
    openProd: "Open production environment",
    openDev: "Open dev environment",
    openTest: "Open test environment",
  },
};

export const wiDetailCs: typeof wiDetailEn = {
  wiHandoffs: {
    kinds: {
      started: "Zahájeno",
      submitted: "Předáno",
      returned: "Vráceno",
      waiting: "Čeká",
      merged: "Sloučeno",
      tested: "Otestováno",
      escalated: "Potřebuje člověka",
      note: "Poznámka",
    },
    agent: "Agent",
    details: "Podrobnosti",
    whatsLeft: "Co zbývá:",
    empty: "Agenti tu zatím nic nenapsali.",
    question: "Otázka",
    recommended: "Doporučeno",
    due: "Do",
    openTitle: "Čeká na člověka",
  },
  wiMerged: {
    mergedInto: "Sloučeno do",
    on: "dne",
    undoMerge: "Vrátit sloučení",
    undone: "Sloučení vráceno",
    undoFailed: "Vrácení se nepodařilo",
  },
  wiContract: {
    title: "Kritéria přijetí",
    ariaLabel: "Kritéria přijetí, jedno na řádek",
    save: "Uložit kritéria",
    cancel: "Zrušit",
    saved: "Kritéria přijetí uložena",
    saveFailed: "Kritéria se nepodařilo uložit",
    byPerson: "Napsal člověk.",
    byAgent: "Napsal agent podle tiketu.",
    againstThese: "Agenti podle nich vyvíjejí, kontrolují a testují.",
    proposed: "Navrhl agent podle tiketu, čekají na schválení.",
    groupDev: "Vývoj (ověří Vratislav při review MR)",
    groupTest: "Test (ověří Prokop po mergi)",
    manual: "ověří člověk ručně",
    pending: "Zatím žádná. Prokop je odvozuje na pozadí, do té doby platí popis tiketu.",
    notDerived: "Úkol pro člověka: kritéria se neodvozují, zadáním je popis.",
    edit: "Upravit kritéria",
    approve: "Schválit kritéria",
    approveTitle:
      "Schválí navržená kritéria tak, jak jsou. Vratislav s Prokopem o nich přestanou vyjednávat a audit se řídí jimi.",
    approved: "Kritéria přijetí schválena",
    approveFailed: "Kritéria se nepodařilo schválit",
    reopen: "Vrátit schválení",
    reopenTitle: "Kritéria se vrátí jako návrh, o kterém rozhodnou Vratislav s Prokopem.",
    reopened: "Schválení vráceno",
    reopenFailed: "Schválení se nepodařilo vrátit",
  },
  wiMergeRequests: {
    title: "Merge requesty",
    none: "Zatím žádný merge request.",
    states: { opened: "otevřený", merged: "mergnutý", closed: "zavřený", locked: "zamčený" },
    placeholder: "Odkaz na MR nebo !číslo",
    link: "Připojit",
    linking: "Připojuji…",
    linked: "Merge request připojen",
    linkFailed: "Merge request se nepodařilo připojit",
    unlink: "Nepatří k tiketu",
    unlinked: "Merge request odpojen",
    unlinkFailed: "Merge request se nepodařilo odpojit",
  },
  wiRelations: {
    title: "Závislosti ({{count}})",
    labels: {
      blocks: "blokuje",
      blocked_by: "blokováno",
      relates: "souvisí s",
      duplicates: "duplikuje",
      duplicated_by: "duplikováno",
      precedes: "předchází",
      follows: "následuje",
      copied_to: "zkopírováno do",
      copied_from: "zkopírováno z",
    },
    added: "Vazba přidána",
    addFailed: "Vazbu se nepodařilo přidat",
    removed: "Vazba odebrána",
    removeFailed: "Vazbu se nepodařilo odebrat",
    removeLink: "Odebrat vazbu",
    remove: "Odebrat {{title}}",
    none: "Zatím žádné závislosti.",
    relationType: "Typ vazby",
    selectPlaceholder: "— vyberte tiket —",
    noWorkItems: "Žádné tikety",
    relatedItem: "Související tiket",
    linkDependency: "Propojit závislost",
    linking: "Propojuji…",
    link: "Propojit",
  },
  wiDescHistory: {
    sources: {
      user: "upraveno",
      suggestion: "schválen návrh AI",
      agent: "změnil agent",
      gitlab: "změněno v GitLabu",
      restore: "obnovena starší verze",
      system: "změnil Hub",
    },
    restored: "Popis obnoven",
    restoreFailed: "Obnovení se nepodařilo",
    history: "Historie",
    title: "Historie popisu",
    description: "Každá verze je popis tak, jak vypadal před uvedenou změnou.",
    loading: "Načítám…",
    empty: "Zatím žádné starší verze. Každá další změna uchová text, který nahradila.",
    showChanges: "Zobrazit změny od té doby",
    showVersion: "Zobrazit tuto verzi",
    restoring: "Obnovuji…",
    restore: "Obnovit tuto verzi",
    emptyVersion: "Prázdné.",
  },
  wiAgentWork: {
    loading: "Načítám…",
    nothingYet: "Zatím nic; řádky se objeví, jak agent pracuje.",
    tookOver: "Vývoj teď držíte vy",
    takeOverFailed: "Převzetí se nepodařilo",
    handedBack: "Předáno zpět Metodějovi",
    handBackFailed: "Předání zpět se nepodařilo",
    workingInLair: "<b>{{agent}}</b> na tom pracuje v doupěti uživatele {{user}}",
    lairSession: "Na tom pracuje session lairu <b>{{session}}</b> uživatele {{user}}",
    working: "<b>{{agent}}</b> na tom pracuje",
    since: "od <date/>",
    noAgent: "Právě na tom nepracuje žádný agent.",
    stopWatching: "Přestat sledovat",
    watch: "Sledovat",
    takingOver: "Přebírám…",
    takeOver: "Převzít vývoj",
    openInLair: "Otevřít v lair",
    handingBack: "Předávám zpět…",
    handBack: "Předat zpět Metodějovi",
    nothingOpened: "Nic se neotevřelo?",
    getLair: "Stáhnout lair",
    orRun: ", nebo spusťte:",
    copyCommand: "Zkopírovat příkaz",
  },
  wiSteps: {
    status: {
      proposed: "Navrženo",
      pending: "K udělání",
      in_progress: "Běží",
      verified: "Ověřeno",
      rejected: "Vzdáno",
      dropped: "Zrušeno",
      promoted: "Vlastní tiket",
    },
    stepTitle: "Název kroku",
    stepCriterion: "Kritérium kroku",
    savingRetries: "Uložením se krok vrátí do nového kola.",
    save: "Uložit",
    cancel: "Zrušit",
    earlierDescription: "Dřívější popis",
    earlierDescriptionHint: "Popis se změnil po ověření tohoto kroku",
    replacedOnActivation: "Nahrazeno při aktivaci",
    attempts_one: "{{count}} pokus",
    attempts_few: "{{count}} pokusy",
    attempts_many: "{{count}} pokusu",
    attempts_other: "{{count}} pokusů",
    whatWasChecked: "Co se kontrolovalo",
    whatWentWrong: "Co se pokazilo",
    edit: "Upravit",
    editHint: "Přepsat název nebo kritérium",
    retry: "Zkusit znovu",
    promote: "Povýšit",
    promoteHint: "Udělat z toho samostatný tiket",
    dropHint: "Není potřeba",
    drop: "Zrušit {{title}}",
    planActivated: "Plán aktivován",
    activateFailed: "Plán se nepodařilo aktivovat",
    stepAdded: "Krok přidán",
    addFailed: "Krok se nepodařilo přidat",
    planUpdated: "Plán aktualizován",
    updateFailed: "Krok se nepodařilo aktualizovat",
    stepUpdated: "Krok aktualizován",
    promoted: "Krok je teď samostatný tiket",
    promoteFailed: "Krok se nepodařilo povýšit",
    title: "Plán",
    progress: "{{verified}}/{{total}} ověřeno",
    none: "Zatím žádný plán. Agent ho navrhne, když si tiket vezme; můžete ho také rozdělit sami níže.",
    proposed_one:
      "{{count}} navržený krok, dokud plán není aktivní, nic se nedělá. Plán agenta kontroluje Vratislav a buď ho aktivuje, nebo vrátí; můžete ho aktivovat i sami.",
    proposed_few:
      "{{count}} navržené kroky, dokud plán není aktivní, nic se nedělá. Plán agenta kontroluje Vratislav a buď ho aktivuje, nebo vrátí; můžete ho aktivovat i sami.",
    proposed_many:
      "{{count}} navrženého kroku, dokud plán není aktivní, nic se nedělá. Plán agenta kontroluje Vratislav a buď ho aktivuje, nebo vrátí; můžete ho aktivovat i sami.",
    proposed_other:
      "{{count}} navržených kroků, dokud plán není aktivní, nic se nedělá. Plán agenta kontroluje Vratislav a buď ho aktivuje, nebo vrátí; můžete ho aktivovat i sami.",
    replaces_one: "Aktivací se zruší krok, který nahrazují.",
    replaces_few: "Aktivací se zruší kroky, které nahrazují.",
    replaces_many: "Aktivací se zruší kroky, které nahrazují.",
    replaces_other: "Aktivací se zruší kroky, které nahrazují.",
    activating: "Aktivuji…",
    activate: "Aktivovat plán",
    stepPlaceholder: "Krok",
    criterionPlaceholder: "Co musí platit, aby se krok považoval za hotový",
    adding: "Přidávám…",
    add: "Přidat",
  },
  wiSidebar: {
    noRuns: "Žádné běhy agentů.",
    tok: "tok",
    cacheRead: "čtení cache",
    cacheWrite: "zápis cache",
    failed: "selhalo",
    running: "běží",
    interrupted: "přerušeno",
    interruptedHint: "Přerušeno restartem při deployi, práce se obnoví",
    mr: "MR",
    estimateNote: "~ odhad podle ceníku: agent běží na předplatném.",
    billing: "Fakturace",
    billingMode: "Způsob fakturace",
    byHours: "Fakturovat odpracované hodiny",
    fixedPrice: "Pevná cena dodávky",
    requiresEstimate: "Vyžaduje odhad",
    fixedAlwaysEstimated: "Práce za pevnou cenu se vždy odhaduje.",
    approvedOffer: "Schválená nabídka (Netlion Cloud)",
    openOffer: "Otevřít schválenou nabídku ↗",
    save: "Uložit",
    invoicedUndone: "Už není označeno jako vyfakturované",
    undoFailed: "Vrácení se nepodařilo",
    invoicedNote: "Označeno jako již vyfakturované dne <date/> — v dalším výkazu se nenabídne.",
    undo: "Vrátit",
    priority: "Priorita",
    priorities: { low: "Nízká", normal: "Normální", high: "Vysoká", urgent: "Urgentní" },
    assignee: "Řešitel",
    unassigned: "— nepřiřazeno —",
    noMembers: "Žádní členové projektu",
    handedToTester: "Předáno testerovi",
    handToTesterFailed: "Předání testerovi se nepodařilo",
    handingOver: "Předávám…",
    handToProkop: "Předat Prokopovi",
    environment: "Prostředí",
    currentEnvironment: "Aktuální prostředí",
    targetRelease: "Cílový release",
    release: "Release",
    none: "— žádný —",
    noReleases: "Žádné releasy",
    changeRelease: "Změnit cílový release",
    repository: "Repozitář",
    projectRepo: "Repozitář projektu",
    changeRepository: "Změnit repozitář, ve kterém se dělá kód",
    team: "Tým",
    saveTeam: "Uložit tým",
    roles: {
      manager: "Manažer",
      developer: "Vývojář",
      tester: "Tester",
      maintainer: "Maintainer",
      productionTester: "Produkční tester",
      productionMaintainer: "Produkční maintainer",
    },
    noCandidates: "Žádní kandidáti",
    onlyCandidate: "(jediný kandidát)",
    locked: "Zamčeno — tým po opuštění stavu <em>{{state}}</em> může měnit jen projektový manažer.",
    openProd: "Otevřít produkční prostředí",
    openDev: "Otevřít vývojové prostředí",
    openTest: "Otevřít testovací prostředí",
  },
};
